/**
 * The record behind a tag: the client for `GET /api/chat/profile/<tag>`.
 *
 * The card that opens when a name is clicked is drawn entirely from what the
 * room already said — that is stage one, it needs no server, and it still
 * works when this module returns nothing (`whoIs` in lib/chat.ts). This is the
 * second half: the two dates the server actually knows about an account, and
 * the three numbers that account's own record claims. It is additive by
 * construction. Every failure here is a card with one section missing, never a
 * card that does not open.
 *
 * Three rules, and they are the inbox's rules (lib/inbox.ts) for the inbox's
 * reasons.
 *
 * 1. **Nothing here may break the chat.** The route is on a Worker that may
 *    not be deployed, behind a session that may have expired, about an account
 *    that may have been deleted since the message was sent. Every one of those
 *    answers `null` rather than throwing, and `null` renders as the card
 *    without a record on it — which is also what a signed-out reader sees and
 *    what a learner who has never pushed progress genuinely is.
 *
 * 2. **What comes back is checked, not trusted.** It arrives as JSON over a
 *    network from a Worker that may be one deploy ahead or behind this bundle.
 *    Every field is validated here, and a field that fails takes only itself
 *    out — the same rule the events route applies to a batch, and the same one
 *    `cleanMentions` applies to an inbox.
 *
 * 3. **One request per card opened, and a small cache behind it.** Clicking
 *    three names in a conversation and clicking back to the first is one of
 *    the two things anybody does with this feature; asking the Worker again
 *    for an answer that cannot have changed in ten seconds is a request spent
 *    on nothing. The cache is per tab, bounded, and deliberately has no
 *    invalidation beyond its age: nothing on this card is live, the dates move
 *    once a day at most, and a record that is a minute stale is a record.
 */
import { cleanSeed } from '@engineer-os/chat';
import { chatUrl } from './chat';

/**
 * One learner's record, as the card shows it.
 *
 * Two halves, kept apart in the type because they are kept apart on the
 * screen. `joined` and `seen` are the server's own clocks and are true. The
 * three numbers under `record` are what that learner's browser pushed, and
 * grading runs in the page (docs/cheating.md) — so they are a claim, the card
 * says so, and nothing here pretends the difference away by flattening the two
 * into one object.
 */
export interface PublicRecord {
  name: string;
  avatarSeed: string | null;
  /** Unix milliseconds, the server's clock. */
  joined: number;
  seen: number;
  /** Null for an account that has never pushed progress from any device. */
  record: { cleared: number; stars: number; xp: number } | null;
}

const SESSION: RequestInit = { credentials: 'include', cache: 'no-store' };

/**
 * How long an answer is worth keeping. Two minutes rather than a number with
 * an argument behind it: everything on this card changes on the scale of days,
 * and the only thing this bounds is how stale a reopened card may be.
 */
const TTL_MS = 120_000;

/** Enough for every distinct person in one sitting, and a ceiling on a Map that never shrinks otherwise. */
const CACHE_MAX = 64;

const cache = new Map<string, { at: number; who: PublicRecord | null }>();

/**
 * The record behind one tag, or null when there is not one to have.
 *
 * Null covers every negative outcome on purpose — no Worker, no session, no
 * account, a deleted account, a record that would not parse — because the card
 * does the same thing for all of them, and a caller that could tell them apart
 * would be a caller with five cases to render and one of them worth rendering.
 */
export async function fetchRecord(tag: string): Promise<PublicRecord | null> {
  const hit = cache.get(tag);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.who;

  let who: PublicRecord | null = null;
  try {
    const r = await fetch(chatUrl(`/api/chat/profile/${tag}`).href, SESSION);
    // 404 is the answer for a signed-out reader, an unknown tag and a deleted
    // account alike, and it is the answer this route gives on purpose so that
    // probing learns nothing. There is nothing to distinguish and nothing to
    // report: all three mean the card shows what the room said.
    if (r.ok) who = readRecord(await r.json());
  } catch {
    /* offline, blocked, or a Worker that has never heard of this route */
  }

  // A miss is cached too. The common case for null is a signed-out reader,
  // and a tab that asked again for every name they clicked would spend a
  // request per click to be told the same thing.
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(tag, { at: Date.now(), who });
  return who;
}

/** Drop everything remembered. For the sign-in and sign-out paths: who may be asked has changed. */
export function forgetRecords(): void {
  cache.clear();
}

/* ── Reading what the Worker said ───────────────────────────────────────── */

const stamp = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.trunc(v) : null;

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0);

/**
 * Exported for the tests, and only for them.
 *
 * The fetch around it cannot be tested here — `chatUrl` reads
 * `import.meta.env`, which the bundler substitutes and a plain Node test
 * runner leaves undefined — and it is three lines of plumbing either way.
 * This is where every decision in the file actually is, so this is what the
 * tests are pointed at.
 */
export function readRecord(body: unknown): PublicRecord | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as { name?: unknown; avatarSeed?: unknown; joined?: unknown; seen?: unknown; record?: unknown };

  // The name is the one field with nothing to fall back to: a card headed by
  // an empty string is a card about nobody, and the room's own name for this
  // person is a better answer than a blank. So a nameless body is no body.
  if (typeof b.name !== 'string' || b.name.trim() === '') return null;
  const joined = stamp(b.joined);
  const seen = stamp(b.seen);
  if (joined === null || seen === null) return null;

  let record: PublicRecord['record'] = null;
  if (typeof b.record === 'object' && b.record !== null) {
    const r = b.record as { cleared?: unknown; stars?: unknown; xp?: unknown };
    record = { cleared: count(r.cleared), stars: count(r.stars), xp: count(r.xp) };
  }

  return {
    name: b.name,
    // Through the protocol's own check, so a seed from a Worker one deploy
    // ahead of this bundle is dropped rather than handed to a renderer that
    // has no rail for it.
    avatarSeed: cleanSeed(b.avatarSeed),
    joined,
    seen,
    record,
  };
}
